import React, { useEffect, useState } from "react";
import { ChevronDown, Users } from "lucide-react";
import { getPipelineAgents } from "../../../api/agent";
import useMyTeam from "../../../hooks/useMyTeam";
import { useAgencyScope } from "../../../components/common/AgencyScopeSwitch";

/**
 * Whose pipeline is on screen: the agent picker shared by the Prospects board
 * and the Dashboard's Pipeline Snapshot.
 *
 * Christian Gravias, Monday 12988442847, 13 Oct 2026: "Add these buttons for
 * super admin or if an agent is part of a team e.g. Spiros/Paul/George to
 * toggle agents"; 14 Oct, on the home page: "Add a button for me to toggle
 * through specific agents".
 *
 * The admin tier gets every agent with how many appraisals they hold, a team
 * member gets their own team. `boardAgent` is the one agent the pipeline is
 * narrowed to; `teamView` is the whole team at once. The Mine / All agents
 * half is the saved 'pipeline' choice (useAgencyScope), so the Dashboard and
 * the board still agree on it. `initialAgentId` opens on one agent, for a
 * link from the Dashboard into Prospects.
 */
export const useAgentScope = ({ initialAgentId = null } = {}) => {
  const { agencyWide, setAgencyWide, canSwitch, canIncludeAgents } = useAgencyScope("pipeline");
  const { members: teamMembers, hasTeam } = useMyTeam();
  const pickerMode = canIncludeAgents ? "admin" : hasTeam ? "team" : null;
  const [boardAgent, setBoardAgent] = useState(initialAgentId ? { id: String(initialAgentId), name: "" } : null);
  const [teamView, setTeamView] = useState(false);
  const [pickerAgents, setPickerAgents] = useState([]);

  // Every agent for the admin tier, busiest first, with a count beside each
  // name, from the same list the appraisal boards' switcher uses. A team's
  // picker is its own members and needs no request.
  useEffect(() => {
    if (pickerMode !== "admin") {
      setPickerAgents([]);
      return undefined;
    }
    let live = true;
    getPipelineAgents("all")
      .then((res) => live && setPickerAgents(res.data?.agents || []))
      .catch(() => live && setPickerAgents([]));
    return () => {
      live = false;
    };
  }, [pickerMode]);

  const agentOptions =
    pickerMode === "team"
      ? teamMembers.map((m) => ({ id: m.id, name: m.is_me ? `${m.name} (me)` : m.name }))
      : pickerAgents;

  // An agent opened from a link arrives with only its id; its name comes from
  // the picker's own list once that has loaded.
  const boardAgentName =
    boardAgent && !boardAgent.name
      ? agentOptions.find((a) => String(a.id) === String(boardAgent.id))?.name || "Agent"
      : boardAgent?.name || "";

  const pickScope = (scope) => {
    if (scope.agent) {
      setBoardAgent({ id: scope.agent.id, name: scope.agent.name });
      setTeamView(false);
      return;
    }
    setBoardAgent(null);
    if (pickerMode === "team") setTeamView(Boolean(scope.team));
    else if (canSwitch) setAgencyWide(Boolean(scope.agencyWide));
  };

  const scopeLabel = boardAgent
    ? boardAgentName
    : pickerMode === "team"
    ? teamView
      ? "Whole team"
      : "My pipeline"
    : agencyWide
    ? "All agents"
    : "My pipeline";

  return {
    agencyWide,
    setAgencyWide,
    canSwitch,
    pickerMode,
    boardAgent,
    boardAgentId: boardAgent?.id || null,
    teamView,
    team: pickerMode === "team" && teamView,
    // "My pipeline" on a team is this user's own leads alone, not the desk
    // the server would otherwise read as "mine" (Paul Jimoulis, Monday
    // 13321217643: "when im in my pipeline i can still see my team leads").
    ownOnly: pickerMode === "team" && !teamView && !boardAgent,
    agentOptions,
    pickScope,
    scopeLabel,
    // Cards say whose they are whenever more than one agent can be on screen.
    showAssigned: agencyWide || teamView || Boolean(boardAgent),
  };
};

/** The dropdown itself. Renders nothing for an agent with no team. */
const AgentScopePicker = ({ scope, className = "" }) => {
  const [open, setOpen] = useState(false);
  const { pickerMode, boardAgent, teamView, agencyWide, canSwitch, agentOptions, pickScope, scopeLabel } = scope;

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [open]);

  if (!pickerMode) return null;

  const pick = (s) => {
    setOpen(false);
    pickScope(s);
  };

  const top =
    pickerMode === "team"
      ? [
          { key: "mine", label: "My pipeline", active: !boardAgent && !teamView, scope: {} },
          { key: "team", label: "Whole team", active: !boardAgent && teamView, scope: { team: true } },
        ]
      : [
          { key: "mine", label: "My pipeline", active: !boardAgent && !agencyWide, scope: {} },
          ...(canSwitch ? [{ key: "all", label: "All agents", active: !boardAgent && agencyWide, scope: { agencyWide: true } }] : []),
        ];

  return (
    <div className={`relative ${className}`} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Switch between agents"
        aria-haspopup="true"
        aria-expanded={open}
        className="px-3.5 py-1.5 rounded-full border border-gray-200 bg-white text-xs font-bold text-gray-700 flex items-center gap-1.5 hover:border-gray-300"
      >
        <Users className="w-3.5 h-3.5 text-[#55C5D0]" />
        {scopeLabel}
        <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-1 w-72 bg-white border border-gray-200 rounded-xl shadow-xl z-40 py-1 max-h-80 overflow-y-auto custom-scrollbar text-sm"
        >
          {top.map((o) => (
            <button
              key={o.key}
              type="button"
              role="menuitem"
              onClick={() => pick(o.scope)}
              className={`w-full text-left px-3 py-2 hover:bg-gray-50 ${o.active ? "font-bold text-[#0e7d86]" : "text-gray-700"}`}
            >
              {o.label}
            </button>
          ))}
          <div className="border-t border-gray-100 my-1" />
          <p className="px-3 py-1 text-[10px] uppercase tracking-wide text-gray-400 font-bold">
            {pickerMode === "team" ? "Your team" : "Every agent"}
          </p>
          {agentOptions.map((a) => (
            <button
              key={a.id}
              type="button"
              role="menuitem"
              onClick={() => pick({ agent: { id: a.id, name: a.name } })}
              className={`w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center justify-between gap-2 ${
                String(boardAgent?.id) === String(a.id) ? "font-bold text-[#0e7d86]" : "text-gray-700"
              }`}
            >
              <span className="truncate">{a.name}</span>
              {a.count != null && <span className="text-[10px] text-gray-400 shrink-0">{a.count}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AgentScopePicker;
